/*
  customsirge_header_menu.js
  Purpose:  Opens the header's mega panel on hover, as well as on the click
            <details> already handles. Nothing else — the panel's contents, the
            tile swap and the white bar behind an open panel are all CSS
            (assets/customsirge_header.css, snippets/header-dropdown-menu.liquid).
  Spec:     client instruction 2026-09-25 ("triggered on hover, not only on
            click"), panel per Figma node 3590:3922.
  Data:     none — reads no settings and no Shopify objects.
  Renders:  nothing; toggles the `open` attribute on the <details> it is given.
  Owns:     window.Customsirge components['header-menu-hover'].
  Owner:    builder(gigli), 2026-09-25

  WHY THIS EXISTS AS JS AT ALL, in a theme that prefers CSS — a hover rule
  (`details:hover > .panel { opacity: 1 }`) does show the panel, but Chrome
  contains the subtree of a CLOSED <details>, so the panel's absolutely
  positioned box resolves to a ~100px shrink-to-fit block instead of stretching
  across the bar (measured: 100px closed vs 1440px open, identical computed
  left:0/right:0). The attribute has to be set for real. Doing it here also
  keeps <details> the one source of truth for "is it open", so the click path,
  Dawn's own focusout-close and the theme editor all keep working unchanged.

  POINTER ONLY. On a touch screen `pointerenter` fires on tap, which would open
  the panel on the same tap that <details> is already toggling — the two fight,
  and the menu flickers open-closed. The hover path is therefore gated on a
  fine, hovering pointer; touch keeps the tap behaviour it had.
*/

(function () {
  'use strict';

  /* Long enough to survive the pointer leaving and re-entering across the seam
     between the bar and the panel (a fast diagonal can register a leave), short
     enough that the panel does not linger once the visitor has moved on. */
  var CLOSE_DELAY = 120;

  Customsirge.register('header-menu-hover', {
    selector: '[data-customsirge-header-menu]',
    init: function (el) {
      var details = el.querySelector('details');
      if (!details) return;

      var fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      var closeTimer = null;

      function cancelClose() {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      }

      function open() {
        if (!fine.matches) return;
        cancelClose();
        if (!details.open) details.open = true;
      }

      function close() {
        if (!fine.matches) return;
        cancelClose();
        closeTimer = setTimeout(function () {
          closeTimer = null;
          /* Keyboard focus inside the panel outranks the pointer having left:
             tabbing through the links must not close the panel under the
             visitor because the mouse happens to be elsewhere. */
          if (el.contains(document.activeElement)) return;
          details.open = false;
        }, CLOSE_DELAY);
      }

      /* Escape closes the panel in ONE press (2026-10-20 accessibility check).
         Dawn closes the <details> on Escape and puts focus back on its
         <summary>; that focus used to land in onFocusIn below and reopen the
         panel straight away, so it took a second press. The flag lets that one
         focus through without reopening. */
      var suppressFocusOpen = false;
      function onKeyDown(e) {
        if (e.key !== 'Escape' || !details.open) return;
        suppressFocusOpen = true;
        cancelClose();
        details.open = false;
        var summary = details.querySelector('summary');
        if (summary) summary.focus();
        setTimeout(function () { suppressFocusOpen = false; }, 0);
      }

      function onFocusIn() {
        cancelClose();
        if (suppressFocusOpen) return;
        if (!details.open) details.open = true;
      }

      /* 2026-10-01 (client): the ACTIVE category — the last one pointed at or
         tabbed to — keeps its band and tiles while the visitor crosses the
         panel to click a tile (only the names themselves switch it; the gap
         and the tiles do not). Cleared on close. */
      var INTENT = 0; /* switch immediately — the client found any delay slow */
      var intentTimer = null;
      function setActive(item) {
        var items = el.querySelectorAll('[data-customsirge-mega-item]');
        for (var i = 0; i < items.length; i++) {
          if (items[i] === item) items[i].setAttribute('data-active', '');
          else items[i].removeAttribute('data-active');
        }
      }
      function onLinkEnter(e) {
        var link = e.target.closest && e.target.closest('.customsirge-header__mega-link');
        if (!link) return;
        var item = link.closest('[data-customsirge-mega-item]');
        if (!item) return;
        clearTimeout(intentTimer);
        if (!INTENT) { setActive(item); return; }
        intentTimer = setTimeout(function () { setActive(item); }, INTENT);
      }
      function onLinkLeave(e) {
        if (e.target.closest && e.target.closest('.customsirge-header__mega-link')) clearTimeout(intentTimer);
      }
      function onItemFocus(e) {
        var link = e.target.closest && e.target.closest('.customsirge-header__mega-link');
        if (link) setActive(link.closest('[data-customsirge-mega-item]'));
      }
      function onToggle() {
        if (!details.open) { clearTimeout(intentTimer); setActive(null); }
      }

      /* The trigger's label is a real link inside the <summary>. A click on it
         would otherwise bubble to the summary and toggle the panel shut on the
         way out — harmless, since the page is navigating, but it flickers.
         Stopping propagation leaves the navigation alone and lets a click
         anywhere ELSE on the summary keep toggling as before. */
      function onLabelClick(e) {
        var label = e.target.closest && e.target.closest('[data-customsirge-menu-label]');
        if (label && el.contains(label)) e.stopPropagation();
      }

      el.addEventListener('click', onLabelClick);
      el.addEventListener('pointerenter', open);
      el.addEventListener('pointerleave', close);
      el.addEventListener('keydown', onKeyDown);
      el.addEventListener('focusin', onFocusIn);
      el.addEventListener('pointerover', onLinkEnter);
      el.addEventListener('pointerout', onLinkLeave);
      el.addEventListener('focusin', onItemFocus);
      details.addEventListener('toggle', onToggle);

      return function () {
        cancelClose();
        clearTimeout(intentTimer);
        el.removeEventListener('click', onLabelClick);
        el.removeEventListener('pointerenter', open);
        el.removeEventListener('pointerleave', close);
        el.removeEventListener('keydown', onKeyDown);
        el.removeEventListener('focusin', onFocusIn);
        el.removeEventListener('pointerover', onLinkEnter);
        el.removeEventListener('pointerout', onLinkLeave);
        el.removeEventListener('focusin', onItemFocus);
        details.removeEventListener('toggle', onToggle);
      };
    },
  });

  /* 2026-10-01 (client): phone menu — each category row expands to its
     products. One open at a time keeps the drawer short. */
  Customsirge.register('drawer-categories', {
    selector: '#menu-drawer',
    init: function (el) {
      function onClick(e) {
        var btn = e.target.closest && e.target.closest('[data-customsirge-drawer-cat]');
        if (!btn || !el.contains(btn)) return;
        var open = btn.getAttribute('aria-expanded') !== 'true';
        var all = el.querySelectorAll('[data-customsirge-drawer-cat]');
        for (var i = 0; i < all.length; i++) {
          var b = all[i];
          var on = b === btn ? open : false;
          b.setAttribute('aria-expanded', on ? 'true' : 'false');
          var list = document.getElementById(b.getAttribute('aria-controls'));
          if (list) list.hidden = !on;
        }
      }
      /* CAPTURE phase + stopPropagation: Dawn's MenuDrawer binds a click on
         every <button> in the drawer to closeSubmenu(), which on these buttons
         closed the whole drawer. Handling the click on the way down stops it
         reaching that listener. */
      function onCapture(e) {
        /* the quiz card opens a pop-up quiz on this page: close the drawer so
           the quiz is not hidden behind it */
        var closer = e.target.closest && e.target.closest('[data-customsirge-drawer-close]');
        if (closer && el.contains(closer)) {
          var drawer = el.closest('header-drawer');
          var summary = drawer && drawer.querySelector('details > summary');
          if (drawer && drawer.closeMenuDrawer && summary) drawer.closeMenuDrawer(e, summary);
          return;
        }
        var btn = e.target.closest && e.target.closest('[data-customsirge-drawer-cat]');
        if (!btn || !el.contains(btn)) return;
        e.stopPropagation();
        onClick(e);
      }
      el.addEventListener('click', onCapture, true);
      return function () {
        el.removeEventListener('click', onCapture, true);
      };
    },
  });
})();
